import styled from '@emotion/styled';
import React, {
  forwardRef,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import theme from '../../theme';
import Heading from '../Heading';
import { motion } from '../Motion';
import { Box, Flex } from '../Rebass';
import Text from '../Text';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';

interface ActionBarProps {
  headline?: string;
  description?: string;
  children?: ReactNode;
  /** Set the color of the ActionBar */
  bg?: string;
  isSticky: boolean;
  /** Set the color of the buttons and decorative elements eg. for a Lodge */
  color?: string;
  /** Set the color of the description */
  descriptionColor?: string;
}

const StyledActionBar = styled(Flex)<{
  isSticky: boolean;
  inView?: boolean;
  color?: string;
  offset?: number;
}>(
  ({ isSticky, inView, color, offset }) => `
  ${color ? `border-left: 5px solid ${color};` : ''}
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: ${theme.zIndex.actionBar};
  height: ${isSticky && inView ? 'auto' : '0px'};
  width: 100%;
  overflow: ${inView ? 'visible' : 'hidden'};
  ${inView ? `border-top: 1px solid ${theme.colors.borderDark};` : ''}
`,
);
const AnimatedActionBar = motion.custom(StyledActionBar);

const ActionBarContainer = styled(Box)`
  height: 0px;
  position: relative;
`;

const HEIGHT = 85;

const ActionBar = forwardRef(
  (
    {
      headline,
      description,
      children,
      bg = 'baseWhite',
      isSticky = false,
      color = theme.colors.brandBrown,
      descriptionColor,
    }: ActionBarProps,
    ref: MutableRefObject<any>,
  ) => {
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
      setIsMounted(true);
    }, []);

    const [offset, setOffset] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [inView, setInView] = useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const actionBarRef = ref || useRef(null);
    const timeout = useRef(null);
    const hasTitleOrDescripton = headline || description;

    const calculateOffset = React.useCallback(() => {
      if (containerRef && containerRef.current) {
        setOffset(containerRef.current?.offsetTop);
      }
    }, [containerRef]);

    useEffect(() => {
      const callback = function (mutationsList, observer) {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            calculateOffset();
          }
        }
      };

      const observer = new MutationObserver(callback);
      observer.observe(document.body, {
        attributes: false,
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }, []);

    useIsomorphicLayoutEffect(() => {
      isMounted && calculateOffset();
    }, [isMounted]);

    useEffect(() => {
      // if it's in the main viewport on initial render, ignore scroll
      if (offset > 0 && offset < window.innerHeight) {
        setInView(true);
      } else {
        if (scrollProgress >= offset + HEIGHT) {
          setInView(true);
        } else {
          setInView(false);
        }
      }
    }, [scrollProgress, offset]);

    useEffect(() => {
      const handleScroll = () => {
        if (timeout.current) {
          window.cancelAnimationFrame(timeout.current);
        }
        timeout.current = window.requestAnimationFrame(() => {
          setScrollProgress(window.pageYOffset + window.innerHeight);
        });
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [offset, timeout]);

    return (
      <ActionBarContainer data-testid="action-bar" ref={containerRef}>
        <AnimatedActionBar
          ref={actionBarRef}
          className="ActionBar"
          width={1}
          px={[2]}
          py={[inView ? 2 : 0]}
          bg={bg}
          justifyContent="space-between"
          isSticky={isSticky}
          inView={inView}
          color={color}
          offset={offset}
          animate={{ height: inView ? 'auto' : '0px' }}
          initial={{ height: '0px' }}
        >
          {(headline || description) && (
            <Flex flexDirection="column">
              {headline && <Heading size="h7">{headline}</Heading>}
              {description && (
                <Text size="tiny" color={descriptionColor}>
                  {description}
                </Text>
              )}
            </Flex>
          )}
          <Flex
            justifyContent={[
              hasTitleOrDescripton ? 'flex-end' : 'space-between',
            ]}
            alignItems="center"
            flex="1 0 auto"
          >
            {children}
          </Flex>
        </AnimatedActionBar>
      </ActionBarContainer>
    );
  },
);

export default ActionBar;
